import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, LogOut, ArrowLeft } from "lucide-react";

import { GameManagement } from "@/components/instructor/GameManagement";
import { CreateTeamDialog } from "@/components/instructor/CreateTeamDialog";
import { StatusBadge } from "@/components/instructor/GameBadges";

export default function InstructorDashboard() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  
  const [games, setGames] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [teams, setTeams] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);

  const fetchGames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games`);
      setGames(response.data);
    } catch (error) {
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompetitions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (err) { console.error("InstructorDashboard:", err.message); }
  }, []);

  const fetchGameDetails = useCallback(async (id) => {
    try {
      const [gameRes, teamsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/games/${id}`),
        axios.get(`${API}/games/${id}/teams`),
        axios.get(`${API}/games/${id}/leaderboard`)
      ]);
      setSelectedGame(gameRes.data);
      setTeams(teamsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (err) { console.error("InstructorDashboard:", err.message); }
  }, []);

  useEffect(() => {
    fetchGames();
    fetchCompetitions();
  }, [fetchGames, fetchCompetitions]);

  useEffect(() => {
    if (gameId) {
      fetchGameDetails(gameId);
    } else if (games.length > 0 && !selectedGame) {
      navigate(`/instructor/game/${games[0].id}`);
    }
  }, [gameId, games, selectedGame, navigate, fetchGameDetails]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="topbar">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/skyboard-logo.png" alt="SkyBoard" className="h-10 w-auto" />
              <div>
                <h1 className="text-lg font-heading font-bold text-white tracking-wide">SKYBOARD</h1>
                <p className="text-xs text-slate-400">Instructor Panel</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {user?.role === "superadmin" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 gap-1.5"
                  data-testid="back-to-admin-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Admin Panel
                </Button>
              )}
              <LanguageSwitcher />
              <ThemeToggle />
              <span className="text-sm text-slate-300">{user?.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Games List */}
        <aside className="w-full md:w-56 lg:w-64 md:min-h-[calc(100vh-4rem)] bg-white border-b md:border-b-0 md:border-r border-slate-200 p-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-heading font-semibold text-slate-700">{t("nav.games")}</h2>
          </div>

          <div className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible scrollbar-hide">
            {games.map((game) => (
              <div
                key={game.id}
                className={`group flex-shrink-0 md:w-full flex items-center gap-1 rounded-lg transition-all text-sm ${
                  gameId === game.id
                    ? "bg-sky-50 border border-sky-200"
                    : "hover:bg-slate-50"
                }`}
              >
                <button
                  onClick={() => navigate(`/instructor/game/${game.id}`)}
                  className="flex-1 text-left px-3 py-2"
                  data-testid={`game-item-${game.id}`}
                >
                  <p className="font-medium text-slate-800 truncate text-sm">{game.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StatusBadge status={game.status} />
                    <span className="text-[11px] text-slate-400">Q{game.current_quarter}/{game.quarters_count}</span>
                  </div>
                </button>
              </div>
            ))}

            {games.length === 0 && !loading && (
              <p className="text-xs text-slate-500 text-center py-4">{t("instructor.noGames")}</p>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 min-w-0">
          {selectedGame ? (
            <GameManagement
              game={selectedGame}
              teams={teams}
              leaderboard={leaderboard}
              competitions={competitions}
              onRefresh={() => { fetchGameDetails(selectedGame.id); fetchGames(); fetchCompetitions(); }}
              onCreateTeamOpen={() => setCreateTeamOpen(true)}
              games={games}
              userRole={user?.role}
            />
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Plane className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t("lobby.selectGame")}</p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Create Team Dialog */}
      <Dialog open={createTeamOpen} onOpenChange={setCreateTeamOpen}>
        <DialogContent>
          <CreateTeamDialog
            gameId={selectedGame?.id}
            onSuccess={(team) => {
              setTeams([...teams, team]);
              setCreateTeamOpen(false);
              toast.success(`Team "${team.name}" created!`);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
